import React from "react";
//#region types
import { TodoReceiptTypeSectionProps } from "models/propTypes/missingDocumentPropTypes";
import { TranslationKeys } from "translations";
import { DateInputProps, SelectInputProps, TextInputProps, WintFormConnect } from "models/propTypes/wintFormPropsTypes";
import { IconDefinitionConstants } from "utils/constants";
import { DimensionEnums } from "models/enums";
import { ReceiptFormTypes } from "models/formTypes";
//#endregion
//#region components
import { useReceiptCategories } from "components/receipt";
import {
  CurrencySelectionInput,
  DateInput,
  DimensionChipSelector,
  EditableTable,
  ExpandableSection,
  ExpandableSectionSummary,
  FormFieldLayout,
  SelectInput,
  TextInput,
  VerticalFormField,
} from "components/common/wintForms";
import { Grid } from "@mui/material";
//#endregion
import { LocalizationHelperInstance, ReceiptHelper } from "utils/helpers";

const TodoReceiptTypeSection: React.FC<TodoReceiptTypeSectionProps> = ({
  connect,
  values,
  amount,
  dimensionProps,
  expandableStepProps,
  fileViewer,
}) => {
  const { receiptCategoriesSelectItems: receiptCategories } = useReceiptCategories();

  const categorySetValue = (value: number | string | undefined, shouldValidate?: boolean) => {
    const categoryConnector = connect("receipt.category");
    const categoryTypeConnector = connect("receipt.categoryType");
    const personsConnector = connect("receipt.persons");
    categoryConnector.setValue(value, shouldValidate);

    const categoryItem = receiptCategories.find((c) => c.value === value);
    if (categoryItem === undefined) {
      categoryTypeConnector.setValue(undefined, false);
    } else {
      categoryTypeConnector.setValue(categoryItem.data.type, false);
    }

    personsConnector.setValue(undefined, false);
  };

  const representation = ReceiptHelper.isRepresentation(values.receipt?.categoryType);

  const dimensionFieldProps = connect("receipt.dimensions");
  const { hasDimensions, required: dimensionsRequired } = dimensionProps;

  return (
    <>
      <ExpandableSection
        alwaysOpen
        summary={
          <ExpandableSectionSummary
            title={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Information)}
          />
        }
        {...expandableStepProps}
      >
        <VerticalFormField<SelectInputProps>
          Component={SelectInput}
          {...connect("receipt.category")}
          setValue={categorySetValue}
          label={TranslationKeys.Receipt.ReceiptRefersTo}
          icon={IconDefinitionConstants.WVat}
          items={receiptCategories}
        />
        {representation ? (
          <>
            <EditableTable<ReceiptFormTypes.ReceiptPerson>
              connect={connect as WintFormConnect<any>}
              name={"receipt.persons"}
              value={values.receipt?.persons ?? []}
              fields={{
                name: {
                  Component: TextInput,
                  extraProps: {
                    label: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Name),
                    icon: IconDefinitionConstants.WUser,
                  },
                  widthColumnCount: 5,
                },
                company: {
                  Component: TextInput,
                  extraProps: {
                    label: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Company),
                  },
                },
              }}
              idField="id"
              defaultRow={{ name: undefined, company: undefined }}
            />
            <VerticalFormField<TextInputProps>
              Component={TextInput}
              {...connect("receipt.intent")}
              label={TranslationKeys.Common.Purpose}
              icon={IconDefinitionConstants.WCommentDots}
            />
          </>
        ) : (
          <></>
        )}
        <Grid container direction="row" columnGap={1} wrap="nowrap">
          <Grid item xs={7}>
            <VerticalFormField<DateInputProps>
              Component={DateInput}
              {...connect("receipt.date")}
              label={TranslationKeys.Common.Date}
              icon={IconDefinitionConstants.WCalendar}
              disabled
            />
          </Grid>
          <Grid item xs={5}>
            <VerticalFormField
              Component={CurrencySelectionInput}
              {...connect("currency")}
              label={TranslationKeys.Common.OriginalCurrency}
            />
          </Grid>
        </Grid>
      </ExpandableSection>
      {hasDimensions ? (
        <ExpandableSection
          summary={
            <ExpandableSectionSummary
              title={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Tags)}
              subText={
                !dimensionsRequired
                  ? LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Optional)
                  : undefined
              }
            />
          }
          initiallyOpen={dimensionsRequired}
          hasErrors={dimensionFieldProps.hasErrors}
          {...expandableStepProps}
        >
          <DimensionChipSelector
            {...dimensionFieldProps}
            type={DimensionEnums.DimensionCategory.Receipt}
            allowDefaultDimensions
            amount={Math.abs(amount ?? 0)}
            currency={values.currency}
            layout={<FormFieldLayout direction="vertical" />}
            fileViewer={fileViewer}
          />
        </ExpandableSection>
      ) : (
        <></>
      )}
    </>
  );
};

export default TodoReceiptTypeSection;
